import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CreditCard, ShieldCheck, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import { local, errMsg } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";

export function SAPayments() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["stripe-config"], queryFn: async () => (await local.get("/stripe/config")).data });
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const webhookUrl = `${window.location.origin}/api/stripe/webhook`;

  const saveMut = useMutation({
    mutationFn: async () => (await local.post("/stripe/config", form)).data,
    onSuccess: (d) => {
      toast.success(`Stripe keys saved — ${d.mode === "live" ? "LIVE" : "TEST"} mode active`);
      setForm({ secret_key: "", publishable_key: "", webhook_secret: "" });
      qc.invalidateQueries({ queryKey: ["stripe-config"] });
    },
    onError: (e) => toast.error(errMsg(e, "Could not save Stripe keys")),
  });

  if (isLoading) return <Loader full />;
  const cfg = data || {};

  return (
    <div data-testid="sa-payments-page" className="max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-teal-500/15 text-teal-400"><CreditCard className="h-5 w-5" /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-white sm:text-3xl">Payments</h1>
          <p className="mt-1 text-sm text-slate-400">Connect Stripe with your own API keys (bring your own key).</p>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <ShieldCheck className={`h-4 w-4 ${cfg.configured ? "text-emerald-400" : "text-slate-500"}`} />
            {cfg.configured ? "Stripe connected" : "Stripe not connected"}
          </div>
          {cfg.configured && (
            <span data-testid="stripe-mode-badge" className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${cfg.mode === "live" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"}`}>
              {cfg.mode} mode
            </span>
          )}
        </div>
        {cfg.configured && (
          <div className="mt-3 grid gap-2 text-xs text-slate-400 sm:grid-cols-2">
            <div>Secret key: <span className="font-mono text-slate-300" data-testid="stripe-secret-masked">{cfg.secret_key_masked}</span></div>
            <div>Publishable: <span className="font-mono text-slate-300">{cfg.publishable_key || "—"}</span></div>
            <div>Webhook secret: <span className="font-mono text-slate-300">{cfg.webhook_secret_masked || "—"}</span></div>
            {cfg.updated_by && <div>Updated by {cfg.updated_by}</div>}
          </div>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }} className="mt-6 space-y-4 rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <p className="text-sm text-slate-300">{cfg.configured ? "Enter new keys to replace the current ones." : "Enter your Stripe keys from the Stripe Dashboard → Developers → API keys."}</p>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Secret key <span className="text-rose-400">*</span></Label>
          <PasswordInput data-testid="stripe-secret-key" value={form.secret_key} onChange={(e) => set("secret_key", e.target.value)} placeholder="sk_live_… or sk_test_…" className="bg-slate-900 text-white" required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Publishable key</Label>
          <Input data-testid="stripe-publishable-key" value={form.publishable_key} onChange={(e) => set("publishable_key", e.target.value)} placeholder="pk_live_… or pk_test_…" className="bg-slate-900 text-white" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-slate-300">Webhook signing secret</Label>
          <PasswordInput data-testid="stripe-webhook-secret" value={form.webhook_secret} onChange={(e) => set("webhook_secret", e.target.value)} placeholder="whsec_…" className="bg-slate-900 text-white" />
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
            Add this endpoint in Stripe → Developers → Webhooks:
            <span className="font-mono text-teal-400" data-testid="stripe-webhook-url">{webhookUrl}</span>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(webhookUrl); toast.success("Webhook URL copied"); }} className="text-slate-400 hover:text-white"><Copy className="h-3.5 w-3.5" /></button>
          </p>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Button data-testid="stripe-save" type="submit" disabled={saveMut.isPending || !form.secret_key} className="bg-teal-600 hover:bg-teal-500">
            {saveMut.isPending ? "Verifying…" : "Save & verify keys"}
          </Button>
          <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
            Stripe Dashboard <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </form>
    </div>
  );
}
